import { localDate, type LocalDate } from '../dates';
import type { DomainEvent } from '../events';
import { singletonId } from '../ids';
import type { Mutation } from '../mutations';
import type { BuildCtx } from '../spending/types';
import type { EffectivePayday } from './paydays';
import {
  isValidLocalDate,
  MAX_INCOME_MINOR,
  validateIncomeSource,
  type IncomeSourceError,
  type IncomeSourceInput,
  type IncomeSourceLike,
} from './schedule';
import { budgetMutation, type BudgetResult } from './types';

export const MAX_INCOME_NOTE = 200;

function sourceValues(input: IncomeSourceInput): Record<string, unknown> {
  return {
    name: input.name.trim(),
    kind: input.kind,
    schedule: input.schedule,
    defaultAmounts: [...input.defaultAmounts],
    currency: 'PHP',
    weekendRule: input.weekendRule,
    startDate: input.startDate,
    ...(input.endDate !== undefined ? { endDate: input.endDate } : {}),
    categoryId: input.categoryId ?? null,
    ...(input.accountId !== undefined ? { accountId: input.accountId } : {}),
  };
}

export function buildAddIncomeSource(
  input: IncomeSourceInput,
  ctx: BuildCtx,
): BudgetResult<{ sourceId: string; mutation: Mutation }, IncomeSourceError> {
  const error = validateIncomeSource(input);
  if (error) return { ok: false, error };
  const sourceId = ctx.newId();
  return {
    ok: true,
    sourceId,
    mutation: budgetMutation(ctx, 'income_sources', 'insert', sourceId, sourceValues(input)),
  };
}

/**
 * Writes every field (a schedule edit is one decision). Confirmed and skipped paydays keep their rows;
 * a new default changes only paydays that haven't happened, so past months never move.
 */
export function buildEditIncomeSource(
  id: string,
  input: IncomeSourceInput,
  ctx: BuildCtx,
): BudgetResult<{ mutation: Mutation }, IncomeSourceError> {
  const error = validateIncomeSource(input);
  if (error) return { ok: false, error };
  return {
    ok: true,
    mutation: budgetMutation(ctx, 'income_sources', 'update', id, sourceValues(input)),
  };
}

/** Soft delete: no more expected paydays. Received income stays in the ledger and in past months. */
export function buildDeleteIncomeSource(id: string, ctx: BuildCtx): Mutation {
  return budgetMutation(ctx, 'income_sources', 'delete', id);
}

/** Undo of a delete: a plain insert on the soft-deleted id brings the source back (conventions §14). */
export function buildRestoreIncomeSource(id: string, ctx: BuildCtx): Mutation {
  return budgetMutation(ctx, 'income_sources', 'insert', id, {});
}

export type PaydayError =
  'bad_amount' | 'bad_date' | 'already_received' | 'not_received' | 'not_skippable';

function validAmount(a: number): boolean {
  return Number.isInteger(a) && a > 0 && a <= MAX_INCOME_MINOR;
}

/**
 * The income row of a received payday has a seeded id, so two devices confirming the same payday
 * offline write one row (their inserts merge), never two. Shorter than the payday's own id, so it
 * fits the 128-character row id limit whenever that does.
 */
export function paydayLedgerId(userId: string, sourceId: string, nominalDate: LocalDate): string {
  return singletonId(userId, `income.${sourceId}.${nominalDate}`);
}

/**
 * Confirm a payday (spec: "Received ₱18,000?"): the payday row becomes 'received' and an income
 * ledger row is written at the confirmed amount, on the day it came. Both ids are seeded and both
 * are plain inserts (no ifAbsent): a second device's confirm merges into the same rows, and a
 * payday that was skipped, or confirmed and undone (soft-deleted), comes back with the new values.
 */
export function buildConfirmPayday(
  userId: string,
  p: EffectivePayday,
  source: Pick<IncomeSourceLike, 'name' | 'categoryId'>,
  got: { amountMinor: number; date: LocalDate; accountId?: string | null },
  ctx: BuildCtx,
): BudgetResult<{ mutations: Mutation[]; event: DomainEvent; ledgerEntryId: string }, PaydayError> {
  if (p.status === 'received') return { ok: false, error: 'already_received' };
  if (!validAmount(got.amountMinor)) return { ok: false, error: 'bad_amount' };
  if (!isValidLocalDate(got.date)) return { ok: false, error: 'bad_date' };
  const ledgerEntryId = paydayLedgerId(userId, p.sourceId, p.nominalDate);
  const payday = budgetMutation(ctx, 'paydays', 'insert', p.id, {
    sourceId: p.sourceId,
    scheduledDate: p.nominalDate,
    actualDate: got.date,
    defaultAmountMinor: p.defaultAmountMinor,
    amountMinor: got.amountMinor,
    currency: 'PHP',
    status: 'received',
    ledgerEntryId,
  });
  const ledger = budgetMutation(ctx, 'ledger_entries', 'insert', ledgerEntryId, {
    date: got.date,
    amountMinor: got.amountMinor,
    currency: 'PHP',
    kind: 'income',
    categoryId: source.categoryId,
    method: null,
    fundId: null,
    // v0.5: the account it landed in (the caller resolves receiveIntoAccount).
    accountId: got.accountId ?? null,
    sourceModule: 'budget',
    sourceId: p.id,
    note: source.name,
    recordedTz: ctx.tz,
  });
  const event: DomainEvent = {
    type: 'payday.received',
    paydayId: p.id,
    sourceId: p.sourceId,
    amountMinor: got.amountMinor,
    date: got.date,
    ledgerEntryId,
    at: ctx.now,
    mutationId: payday.id,
  };
  return { ok: true, mutations: [payday, ledger], event, ledgerEntryId };
}

/** A bonus that didn't come: skipped for this date only; the source's default stays the same. */
export function buildSkipPayday(
  p: EffectivePayday,
  ctx: BuildCtx,
): BudgetResult<{ mutation: Mutation; event: DomainEvent }, PaydayError> {
  if (p.status === 'received') return { ok: false, error: 'already_received' };
  // Spec: only bonuses can be skipped; an unconfirmed salary stays expected (Ivan, 2026-11-02).
  if (p.sourceKind !== 'bonus') return { ok: false, error: 'not_skippable' };
  const mutation = budgetMutation(ctx, 'paydays', 'insert', p.id, {
    sourceId: p.sourceId,
    scheduledDate: p.nominalDate,
    actualDate: null,
    defaultAmountMinor: p.defaultAmountMinor,
    amountMinor: null,
    currency: 'PHP',
    status: 'skipped',
    ledgerEntryId: null,
  });
  const event: DomainEvent = {
    type: 'payday.skipped',
    paydayId: p.id,
    sourceId: p.sourceId,
    date: p.date,
    at: ctx.now,
    mutationId: mutation.id,
  };
  return { ok: true, mutation, event };
}

/** Undo a confirm or a skip: the payday row (and its income row) are soft-deleted, so it is expected again. */
export function buildUndoPayday(p: EffectivePayday, ctx: BuildCtx): Mutation[] {
  if (!p.stored) return [];
  const out = [budgetMutation(ctx, 'paydays', 'delete', p.id)];
  if (p.ledgerEntryId) out.push(budgetMutation(ctx, 'ledger_entries', 'delete', p.ledgerEntryId));
  return out;
}

/** Spec §8a "edits ripple": changing a received amount or day updates the payday and its ledger row together. */
export function buildEditReceivedPayday(
  p: EffectivePayday,
  patch: { amountMinor?: number; date?: LocalDate; accountId?: string | null },
  ctx: BuildCtx,
): BudgetResult<{ mutations: Mutation[] }, PaydayError> {
  if (p.status !== 'received' || !p.ledgerEntryId) return { ok: false, error: 'not_received' };
  if (patch.amountMinor !== undefined && !validAmount(patch.amountMinor)) {
    return { ok: false, error: 'bad_amount' };
  }
  if (patch.date !== undefined && !isValidLocalDate(patch.date)) {
    return { ok: false, error: 'bad_date' };
  }
  const payday: Record<string, unknown> = {};
  const ledger: Record<string, unknown> = {};
  if (patch.amountMinor !== undefined && patch.amountMinor !== p.amountMinor) {
    payday.amountMinor = patch.amountMinor;
    ledger.amountMinor = patch.amountMinor;
  }
  if (patch.date !== undefined && patch.date !== p.date) {
    payday.actualDate = patch.date;
    ledger.date = patch.date;
  }
  // v0.5: the account it landed in lives on the income row only (the caller passes a change).
  if (patch.accountId !== undefined) ledger.accountId = patch.accountId;
  if (Object.keys(ledger).length === 0) return { ok: true, mutations: [] };
  return {
    ok: true,
    mutations: [
      ...(Object.keys(payday).length > 0
        ? [budgetMutation(ctx, 'paydays', 'update', p.id, payday)]
        : []),
      budgetMutation(ctx, 'ledger_entries', 'update', p.ledgerEntryId, ledger),
    ],
  };
}

export interface OtherIncomeDraft {
  amountMinor: number;
  date?: LocalDate;
  note?: string | null;
  categoryId?: string | null;
  /** v0.5: the account it landed in (resolved by the caller). */
  accountId?: string | null;
}

/**
 * One-off income Budget owns (side job, refund, gift): an income row with no payday behind it.
 * It can be edited and deleted any time; a payday's income row changes only through its payday.
 */
export function isOtherIncome(e: {
  kind?: string;
  sourceModule: string;
  sourceId?: string | null;
}): boolean {
  return e.kind === 'income' && e.sourceModule === 'budget' && e.sourceId == null;
}

export interface OtherIncomeFields {
  amountMinor: number;
  date: LocalDate;
  note: string | null;
  /** v0.5: the account it landed in. */
  accountId?: string | null;
}

/** Writes only the fields that changed; `undo` puts them back. */
export function buildEditOtherIncome(
  id: string,
  before: OtherIncomeFields,
  patch: Partial<OtherIncomeFields>,
  ctx: BuildCtx,
): BudgetResult<
  { mutation: Mutation | null; undo: Partial<OtherIncomeFields> },
  PaydayError | 'note_too_long'
> {
  const values: Record<string, unknown> = {};
  const undo: Partial<OtherIncomeFields> = {};
  if (patch.amountMinor !== undefined && patch.amountMinor !== before.amountMinor) {
    if (!validAmount(patch.amountMinor)) return { ok: false, error: 'bad_amount' };
    values.amountMinor = patch.amountMinor;
    undo.amountMinor = before.amountMinor;
  }
  if (patch.date !== undefined && patch.date !== before.date) {
    if (!isValidLocalDate(patch.date)) return { ok: false, error: 'bad_date' };
    values.date = patch.date;
    undo.date = before.date;
  }
  if (patch.note !== undefined) {
    const cleaned = (patch.note ?? '').trim().replace(/\s+/g, ' ');
    if (cleaned.length > MAX_INCOME_NOTE) return { ok: false, error: 'note_too_long' };
    const note = cleaned === '' ? null : cleaned;
    if (note !== before.note) {
      values.note = note;
      undo.note = before.note;
    }
  }
  if (patch.accountId !== undefined && patch.accountId !== (before.accountId ?? null)) {
    values.accountId = patch.accountId;
    undo.accountId = before.accountId ?? null;
  }
  if (Object.keys(values).length === 0) return { ok: true, mutation: null, undo };
  return {
    ok: true,
    mutation: budgetMutation(ctx, 'ledger_entries', 'update', id, values),
    undo,
  };
}

/** One-off income (side job, refund, gift): an income ledger row with no payday behind it. */
export function buildAddOtherIncome(
  draft: OtherIncomeDraft,
  ctx: BuildCtx,
): BudgetResult<{ entryId: string; mutation: Mutation }, PaydayError | 'note_too_long'> {
  if (!validAmount(draft.amountMinor)) return { ok: false, error: 'bad_amount' };
  const date = draft.date ?? localDate(ctx.now, ctx.tz);
  if (!isValidLocalDate(date)) return { ok: false, error: 'bad_date' };
  const note = (draft.note ?? '').trim().replace(/\s+/g, ' ');
  if (note.length > MAX_INCOME_NOTE) return { ok: false, error: 'note_too_long' };
  const entryId = ctx.newId();
  const mutation = budgetMutation(ctx, 'ledger_entries', 'insert', entryId, {
    date,
    amountMinor: draft.amountMinor,
    currency: 'PHP',
    kind: 'income',
    categoryId: draft.categoryId ?? null,
    method: null,
    fundId: null,
    // v0.5: the account it landed in (the caller resolves receiveIntoAccount).
    accountId: draft.accountId ?? null,
    sourceModule: 'budget',
    sourceId: null,
    note: note === '' ? null : note,
    recordedTz: ctx.tz,
  });
  return { ok: true, entryId, mutation };
}
